import { sizes } from '@lmc/common/constants'
import type { RouterProps } from '@lmc/common/types'
import type { Home, Need, ResidentNeed } from '@lmc/ts-common'
import { Text } from '@lmc/ui-kit'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useResident from 'hooks/useResident'
import useResidentNeeds, {
  type ResidentNeedV6,
  useResidentNeedAssign,
  useResidentNeedUpdate
} from 'hooks/useResidentNeeds'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  connect,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam,
  useSelector
} from 'react-redux'
import { getResidentNeeds } from 'selectors/NeedsAndSkills'
import { NoDataFound } from '../../../../components/generic/NoDataFound'
import Operations from '../../../../operations'
import { DeleteSkillNeedModal } from '../../../../screens/AccountsSettings/SkillsAndNeeds/components'
import { withRouter } from '../../../../withRouter'
import {
  NeedsModal,
  ResidentNeedDeleteModal,
  ResidentNeedsHeaderSection,
  ResidentNeedsList,
  ResidentNeedsTable
} from './components'

export type ProfileNeedOption = { name: string; needs: Need[] }

interface StateProps {
  needs?: ResidentNeed[]
  profileNeedOptions?: ProfileNeedOption[]
  carePlanNominative?: string
  preferredPeopleNominative?: string
}

interface DispatchProps {
  fetchResidentNeedOptions?: () => Promise<unknown>
  fetchResidentNeeds?: (id: string) => Promise<unknown>
  addNeed?: (need: ResidentNeed, id: string) => Promise<unknown>
  updateNeed?: (
    need: ResidentNeed,
    id: string,
    needId: string
  ) => Promise<unknown>
  deleteNeed?: (needId: string, residentId: string) => Promise<unknown>
  router: RouterProps
}

type Props = StateProps & DispatchProps & { testId?: string }

// ── New v6 screen ────────────────────────────────────────────────────────────

function ResidentNeedsScreenV6({
  residentId,
  testId
}: {
  residentId: string
  testId?: string
}): React.ReactElement {
  const carePlanNominative = useSelector(
    (state: {
      global: { home: Home & { preferredCarePlansNominative: string } }
    }) => state.global.home.preferredCarePlansNominative
  )

  const { resident, isLoading: isResidentLoading } = useResident({
    id: residentId
  })
  const residentName = isResidentLoading
    ? undefined
    : `${resident?.name?.first} ${resident?.name?.last}`
  const residentFirstName = isResidentLoading
    ? undefined
    : resident?.name?.first

  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editNeed, setEditNeed] = useState<ResidentNeedV6 | null>(null)
  const [deleteNeed, setDeleteNeed] = useState<ResidentNeedV6 | null>(null)

  // Fetch list for pre-computing assigned IDs (used in add modal)
  const { needs } = useResidentNeeds({ id: residentId })

  const assignedV6NeedIds = useMemo(() => needs.map((n) => n.need.id), [needs])

  // Mutations
  const { assignNeed, isAssigning } = useResidentNeedAssign(residentId, () =>
    setAddModalOpen(false)
  )
  const { updateNeed: updateResidentNeed, isUpdating } = useResidentNeedUpdate(
    residentId,
    () => setEditNeed(null)
  )

  return (
    <div
      data-testid={
        testId ? `${testId}-resident-needs-screen` : 'resident-needs-screen'
      }
    >
      {/* Add modal */}
      <NeedsModal
        isModalVisible={addModalOpen}
        handleToggleModal={() => setAddModalOpen(false)}
        residentId={residentId}
        onV6Assign={assignNeed}
        isV6Loading={isAssigning}
        assignedV6NeedIds={assignedV6NeedIds}
        testId={testId}
      />

      {/* Edit modal */}
      <NeedsModal
        isModalVisible={!!editNeed}
        handleToggleModal={() => setEditNeed(null)}
        residentId={residentId}
        residentNeedV6={editNeed}
        onV6Update={(payload) =>
          updateResidentNeed({
            assignmentId: editNeed!.id,
            notes: payload.notes
          })
        }
        isV6Loading={isUpdating}
        testId={testId}
      />

      {/* Delete confirmation modal */}
      <ResidentNeedDeleteModal
        isModalVisible={!!deleteNeed}
        need={deleteNeed}
        residentId={residentId}
        onClose={() => setDeleteNeed(null)}
        testId={testId ? `${testId}-needs-delete-modal` : 'needs-delete-modal'}
      />

      <div
        style={styles.container}
        data-testid={testId ? `${testId}-needs-content` : 'needs-content'}
      >
        <ResidentNeedsHeaderSection
          residentName={residentName}
          residentFirstName={residentFirstName}
          carePlanNominative={carePlanNominative}
          handleToggleModal={() => setAddModalOpen(true)}
          testId={testId}
        />
        <ResidentNeedsList
          residentId={residentId}
          onEdit={setEditNeed}
          onDelete={setDeleteNeed}
          onAssign={() => setAddModalOpen(true)}
          testId={testId}
        />
      </div>
    </div>
  )
}

// ── Legacy screen ─────────────────────────────────────────────────────────────

type LegacyProps = Omit<Props, 'router'> & { residentId: string }

function ResidentNeedsScreenLegacy({
  addNeed,
  updateNeed,
  deleteNeed,
  needs,
  profileNeedOptions,
  residentId,
  carePlanNominative,
  preferredPeopleNominative,
  fetchResidentNeedOptions,
  fetchResidentNeeds,
  testId
}: LegacyProps): React.ReactElement {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openDelete, setOpenDelete] = useState(false)
  const [profileNeedDetail, setProfileNeedDetail] = useState<ResidentNeed>()

  const { resident, isLoading } = useResident({ id: residentId })

  const residentFirstName = isLoading ? undefined : resident?.name?.first
  const residentName = isLoading
    ? undefined
    : `${resident?.name?.first} ${resident?.name?.last}`

  const loadResidentNeeds = async () => {
    await fetchResidentNeeds?.(residentId).then(() => setLoading(false))
  }

  const handleToggleModal = () => {
    if (open) {
      setProfileNeedDetail(undefined)
    }
    setOpen(!open)
  }

  const handleToogleDeleteModal = () => {
    setOpenDelete(!openDelete)
    setOpen(false)
  }

  React.useEffect(() => {
    fetchResidentNeedOptions?.()
    loadResidentNeeds()
  }, [residentId])

  const hasNeeds = !!needs?.length

  const needOptions: Need[] = []
  profileNeedOptions?.forEach((item) => {
    item.needs?.forEach((need) => {
      needOptions.push(need)
    })
  })

  const handleDelete = async () => {
    try {
      const res = await deleteNeed?.(
        profileNeedDetail?.id as string,
        residentId
      )
      if (res) {
        setProfileNeedDetail(undefined)
        handleToogleDeleteModal()
        handleToggleModal()
        loadResidentNeeds()
        setOpen(false)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  return (
    <div
      data-testid={
        testId ? `${testId}-resident-needs-screen` : 'resident-needs-screen'
      }
    >
      <DeleteSkillNeedModal
        isModalVisible={openDelete}
        handleToogleDeleteModal={handleToogleDeleteModal}
        onConfirm={handleDelete}
        title={t('skillsAndNeeds.areYouSureNeed')}
        data-testid={
          testId ? `${testId}-needs-delete-modal` : 'needs-delete-modal'
        }
      >
        <Text as="p2">
          {t('skillsAndNeeds.byRemovingNeed', {
            variable: preferredPeopleNominative?.toLowerCase()
          })}
        </Text>
      </DeleteSkillNeedModal>
      <NeedsModal
        isModalVisible={open}
        handleToogleDeleteModal={handleToogleDeleteModal}
        handleToggleModal={handleToggleModal}
        addNeed={addNeed}
        updateNeed={updateNeed}
        profileNeedOptions={profileNeedOptions}
        residentId={residentId}
        profileNeedDetail={profileNeedDetail}
        needs={needs}
        carePlanNominative={carePlanNominative}
        testId={testId}
      />
      <div
        style={styles.container}
        data-testid={testId ? `${testId}-needs-content` : 'needs-content'}
      >
        <ResidentNeedsHeaderSection
          residentName={residentName}
          residentFirstName={residentFirstName}
          carePlanNominative={carePlanNominative}
          handleToggleModal={handleToggleModal}
          testId={testId}
        />
        {!loading && hasNeeds ? (
          <ResidentNeedsTable
            loading={loading}
            needs={needs}
            needOptions={needOptions}
            carePlanNominative={carePlanNominative as string}
            setProfileNeedDetail={setProfileNeedDetail}
            testId={testId}
          />
        ) : (
          !loading && (
            <NoDataFound
              heading={t('skillsAndNeeds.nothingToSee')}
              text={t('skillsAndNeeds.addNeedsToProfile')}
              linkText={t('skillsAndNeeds.findOutMore')}
              url="https://support.logmycare.co.uk/en/articles/6987442-how-to-set-up-skills-for-your-service"
            />
          )
        )}
      </div>
    </div>
  )
}

// ── Router (thin, connected) ──────────────────────────────────────────────────

export function ResidentNeedsScreen({
  router,
  testId,
  ...legacyProps
}: Props): React.ReactElement {
  const residentId = router.params.id
  const { data: flags } = useFeatureFlags({
    flagNames: ['mods_needs_resident_profile']
  })
  const isNewNeedsEnabled = flags.mods_needs_resident_profile?.enabled

  if (isNewNeedsEnabled) {
    return <ResidentNeedsScreenV6 residentId={residentId} testId={testId} />
  }

  return (
    <ResidentNeedsScreenLegacy
      residentId={residentId}
      testId={testId}
      {...legacyProps}
    />
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    paddingTop: '24px'
  }
}

export const mapStateToProps = (
  state: {
    data: {
      needs: ResidentNeed[]
      ['skill-need-templates/categories']: Need[]
    }
    global: {
      home: {
        preferredCarePlansNominative: string
        preferredPeopleNominative: string
      }
    }
  },
  props: { router: { params: { id: string } } }
) => ({
  needs: getResidentNeeds(state, props),
  profileNeedOptions: state.data['skill-need-templates/categories'],
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative
})

const mapDispatchToProps = (dispatch: (arg0: unknown) => unknown) => ({
  fetchResidentNeedOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { needs: 1 })
    ),
  fetchResidentNeeds: (id: string) =>
    dispatch(
      Operations.loadList(`needs`, '', true, `/api/list/residents/${id}/needs`)
    ),
  addNeed: (need: ResidentNeed, id: string) =>
    dispatch(
      Operations.createListItem({
        listId: `needs`,
        itemId: '',
        json: need,
        url: `list/residents/${id}/needs`
      })
    ),
  updateNeed: (need: ResidentNeed, id: string, needId: string) =>
    dispatch(
      Operations.patchListItem({
        listId: `needs`,
        itemId: '',
        json: need,
        url: `list/residents/${id}/needs/${needId}`
      })
    ),
  deleteNeed: (needId: string, residentId: string) =>
    dispatch(
      Operations.deleteListItem({
        listId: `needs`,
        itemId: '',
        url: `list/residents/${residentId}/needs/${needId}`
      })
    )
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    mapStateToProps as unknown as MapStateToPropsParam<
      StateProps,
      Props,
      unknown
    >,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(ResidentNeedsScreen)
)
